import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { authErrorMessage } from "../lib/authError";
import AdminDashboard from "./admin/AdminDashboard";
import AdminClients from "./admin/AdminClients";
import AdminProducts from "./admin/AdminProducts";
import AdminNews from "./admin/AdminNews";
import AdminAppointments from "./admin/AdminAppointments";
import AdminImport from "./admin/AdminImport";
import AdminCustomerImport from "./admin/AdminCustomerImport";
import AdminCategories from "./admin/AdminCategories";
import AdminBrands from "./admin/AdminBrands";
import AdminSecurity from "./admin/AdminSecurity";
import AdminPushTest from "./admin/AdminPushTest";
import AdminAnalytics from "./admin/AdminAnalytics";
import AdminBirthdays from "./admin/AdminBirthdays";
import AdminCrm from "./admin/AdminCrm";
import AdminAtelier from "./admin/AdminAtelier";
import AdminBroadcast from "./admin/AdminBroadcast";
import AdminMetaAds from "./admin/AdminMetaAds";
import AdminVideoLessons from "./admin/AdminVideoLessons";
import AdminStaff from "./admin/AdminStaff";
import AdminTranslations from "./admin/AdminTranslations";
import AdminArrivals from "./admin/AdminArrivals";
import AdminStock from "./admin/AdminStock";
import AdminInvoices from "./admin/AdminInvoices";
import AdminAccounting from "./admin/AdminAccounting";
import AdminInventoryAudit from "./admin/AdminInventoryAudit";
import AdminReplyTemplates from "./admin/AdminReplyTemplates";
import AdminKnowledgeBase from "./admin/AdminKnowledgeBase";
import AdminAutopilot from "./admin/AdminAutopilot";
import AdminShiftHistory from "./admin/AdminShiftHistory";
import NotificationsPanel from "./admin/NotificationsPanel";
import Logo from "../components/Logo";
import DutyToggle from "../components/DutyToggle";
import OnShiftBar from "../components/OnShiftBar";
import TodayTasksBar from "../components/TodayTasksBar";
import { LayoutDashboard, Users, ShoppingBag, Newspaper, Calendar, LogOut, Menu, X, Upload, Database, FolderTree, Award, Shield, TrendingUp, Cake, Briefcase, Eye, UserCog, Languages, PackagePlus, Boxes, BarChart3, Scan, Bell, ChevronDown, ChevronRight, FileText, MessageSquareQuote, BookOpen, Bot, Clock, GraduationCap, KeyRound, Scissors, Send, Target, PlayCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Sections grouped for the sidebar. Every group collapses/expands independently
// so the menu doesn't sprawl. `defaultOpen: true` on groups the user needs most
// of the time (Обзор, Клиенты, Каталог). All items keep their previous ids so
// deep-linking / test-ids continue to work.
const SECTION_GROUPS = [
  {
    id: "overview", label: "Обзор", defaultOpen: true,
    items: [
      { id: "dashboard", label: "Дашборд", icon: LayoutDashboard },
      { id: "analytics", label: "Аналитика", icon: TrendingUp },
    ],
  },
  {
    id: "clients", label: "Клиенты", defaultOpen: true,
    items: [
      { id: "crm", label: "Mini CRM", icon: Briefcase },
      { id: "atelier", label: "Ателье (перешивы)", icon: Scissors },
      { id: "broadcast", label: "Рассылки WhatsApp", icon: Send, seniorOnly: true },
      { id: "meta_ads", label: "Реклама Meta", icon: Target, seniorOnly: true },
      { id: "clients", label: "База клиентов", icon: Users },
      { id: "appointments", label: "Записи", icon: Calendar },
      { id: "birthdays", label: "Дни рождения", icon: Cake },
      { id: "reply_templates", label: "Шаблоны ответов", icon: MessageSquareQuote },
      { id: "knowledge_base", label: "База знаний (ИИ)", icon: BookOpen },
      { id: "autopilot", label: "Autopilot (AI)", icon: Bot },
      { id: "shift_history", label: "История смен", icon: Clock },
      { id: "academy", label: "Академия", icon: GraduationCap, external: "/academy" },
      { id: "video_lessons", label: "Видеоуроки", icon: PlayCircle, seniorOnly: true },
    ],
  },
  {
    id: "catalog", label: "Каталог", defaultOpen: true,
    items: [
      { id: "products", label: "Товары", icon: ShoppingBag },
      { id: "categories", label: "Категории", icon: FolderTree },
      { id: "brands", label: "Бренды", icon: Award },
      { id: "news", label: "Новости", icon: Newspaper },
    ],
  },
  {
    id: "warehouse", label: "Склад и финансы", defaultOpen: false,
    items: [
      { id: "arrivals", label: "Поступления", icon: PackagePlus, costOnly: true },
      { id: "stock", label: "Остатки", icon: Boxes, financeOnly: true },
      { id: "inventory_audit", label: "Инвентаризация", icon: Scan, financeOnly: true },
      { id: "accounting", label: "Финансы", icon: BarChart3, costOnly: true },
      { id: "invoices", label: "Счета на оплату", icon: FileText, billingAccess: true },
    ],
  },
  {
    id: "settings", label: "Настройки", defaultOpen: false,
    items: [
      { id: "staff", label: "Сотрудники", icon: UserCog, adminOnly: true },
      { id: "translations", label: "Переводы", icon: Languages, adminOnly: true },
      { id: "security", label: "Безопасность", icon: Shield },
      { id: "push_test", label: "Push-уведомления", icon: Bell, adminOnly: true },
      { id: "import", label: "Импорт x2pos", icon: Upload },
      { id: "customers_import", label: "Импорт клиентов", icon: Database },
    ],
  },
];

// Flatten helper — retains previous `SECTIONS` API (used for the section title lookup).
const SECTIONS = SECTION_GROUPS.flatMap((g) => g.items);

// Role → visible sections. `financeOnly` means admin+senior_manager+accountant.
// `costOnly` — поступления и себестоимость: только владелец и бухгалтер.
// `billingAccess` additionally lets sales managers issue invoices.
const canSee = (section, role) => {
  if (section.adminOnly) return role === "admin";
  if (section.costOnly) return ["admin", "accountant"].includes(role);
  if (section.seniorOnly) return ["admin", "senior_manager"].includes(role);
  if (section.financeOnly) return ["admin", "senior_manager", "accountant"].includes(role);
  if (section.billingAccess) return ["admin", "senior_manager", "accountant", "manager"].includes(role);
  return true;
};

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => {
    let expired = new URLSearchParams(window.location.search).get("session_expired");
    try {
      if (!expired && sessionStorage.getItem("session_expired")) expired = "1";
      sessionStorage.removeItem("session_expired");
    } catch { /* ignore */ }
    return expired
      ? "Не удалось подтвердить сессию. Войдите снова с действующим паролем."
      : "";
  });
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("password"); // 'password' | '2fa'
  const [challengeToken, setChallengeToken] = useState(null);
  const [code, setCode] = useState("");
  const { login } = useAuth();

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin-login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true });
      if (res.data.requires_2fa) {
        setChallengeToken(res.data.challenge_token);
        setStage("2fa");
        setCode("");
      } else {
        login(res.data.user);
        onSuccess();
      }
    } catch (err) {
      setError(authErrorMessage(err, "Неверный email или пароль"));
    } finally { setLoading(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin-login-2fa`, { challenge_token: challengeToken, code }, { withCredentials: true });
      login(res.data.user);
      onSuccess();
    } catch (err) {
      setError(authErrorMessage(err, "Неверный код"));
    } finally { setLoading(false); }
  };

  return (
    <div className="light-section" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FDFBF7" }}>
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "44px 40px", width: "400px", maxWidth: "92vw", boxShadow: "0 6px 32px rgba(0,0,0,0.08)" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: "10px" }}>
          <Logo size="md" variant="light" glow={false} />
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#999", textAlign: "center", letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: "32px", fontWeight: 700 }}>ADMIN PANEL</div>

        {stage === "password" ? (
          <form onSubmit={submitPassword} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
              autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" autoComplete="username"
              style={{ background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "13px 16px", fontSize: "16px", fontFamily: "Manrope", outline: "none", width: "100%", color: "#0A0A0A" }} />
            <input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Пароль"
              autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="current-password"
              style={{ background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "13px 16px", fontSize: "16px", fontFamily: "Manrope", outline: "none", width: "100%", color: "#0A0A0A" }} />
            {error && <p role="alert" data-testid="admin-login-error" style={{ color: "#dc2626", fontFamily: "Manrope", fontSize: "13px" }}>{error}</p>}
            <button data-testid="admin-login-btn" type="submit" disabled={loading}
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", padding: "13px", borderRadius: "4px", border: "none", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "6px" }}>
              {loading ? "Вход..." : "Войти"}
            </button>
          </form>
        ) : (
          <form onSubmit={submit2fa} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", background: "#F9FAFB", borderRadius: "6px", border: "1px solid #E5E7EB" }}>
              <Shield size={18} style={{ color: "#C5A059" }} />
              <div style={{ flex: 1, fontFamily: "Manrope", fontSize: "12.5px", color: "#374151", lineHeight: 1.5 }}>
                Введите 6-значный код из приложения-аутентификатора
              </div>
            </div>
            <input data-testid="admin-2fa-code" type="text" value={code} onChange={e => setCode(e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 11))} placeholder="000 000" autoFocus
              style={{ background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "14px 16px", fontFamily: "monospace", fontSize: "20px", letterSpacing: "0.25em", textAlign: "center", outline: "none", width: "100%", color: "#0A0A0A" }} />
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#9CA3AF", textAlign: "center", margin: "-4px 0 0" }}>
              или используйте резервный код (XXXXX-XXXXX)
            </div>
            {error && <p style={{ color: "#dc2626", fontFamily: "Manrope", fontSize: "13px" }}>{error}</p>}
            <button data-testid="admin-2fa-submit" type="submit" disabled={loading || !code}
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", padding: "13px", borderRadius: "4px", border: "none", cursor: loading ? "wait" : "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "6px", opacity: !code ? 0.5 : 1 }}>
              {loading ? "Проверка..." : "Войти"}
            </button>
            <button type="button" onClick={() => { setStage("password"); setError(""); setCode(""); setChallengeToken(null); }}
              style={{ background: "transparent", border: "none", color: "#6B7280", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", marginTop: "-6px" }}>
              ← Назад к паролю
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  // Раздел запоминаем на время вкладки: если браузер на телефоне выгрузил
  // страницу из памяти, менеджер вернётся туда же, где работал.
  const [section, setSection] = useState(() => {
    try { return sessionStorage.getItem("admin_section") || "dashboard"; }
    catch { return "dashboard"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("admin_section", section); } catch { /* noop */ }
  }, [section]);
  const [loginOverride, setLoginOverride] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);

  const fetchUnread = async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications/feed`, { withCredentials: true });
      setNotifUnread(r.data.unread || 0);
    } catch { /* noop */ }
  };
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Cross-section navigation: rows in Склад/Поступления/Инвентаризация can
  // dispatch `admin:navigate` to jump straight into the Товары editor.
  useEffect(() => {
    const handler = (ev) => {
      const s = ev?.detail?.section;
      if (s) setSection(s);
    };
    window.addEventListener("admin:navigate", handler);
    return () => window.removeEventListener("admin:navigate", handler);
  }, []);
  // Persist collapsed group state per-browser so managers keep their layout
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("adminOpenGroups");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return SECTION_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: g.defaultOpen }), {});
  });
  const toggleGroup = (gid) => setOpenGroups((prev) => {
    const next = { ...prev, [gid]: !prev[gid] };
    try { localStorage.setItem("adminOpenGroups", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const isAdmin = loginOverride || (user && ["admin", "manager", "senior_manager", "accountant"].includes(user.role));
  const isTrainee = user && user.role === "trainee";

  // Сессию могли закрыть извне (админ сменил пароль / выбросил сотрудника):
  // как только пользователь пропал, локальный «вход» тоже сбрасываем, иначе
  // панель осталась бы на экране с мёртвым токеном.
  useEffect(() => {
    if (!loading && !user && loginOverride) setLoginOverride(false);
  }, [loading, user, loginOverride]);

  if (loading) return <div style={{ background: "#FDFBF7", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Manrope", color: "#666" }}>Загрузка...</div>;
  // Trainees don't belong in the admin panel — pack them off to the academy.
  if (isTrainee) return <Navigate to="/academy" replace />;
  if (!isAdmin) return <AdminLogin onSuccess={() => setLoginOverride(true)} />;

  const handleLogout = async () => { await logout(); setLoginOverride(false); };

  const sectionDef = SECTIONS.find((s) => s.id === section);
  const sectionAllowed = !sectionDef || canSee(sectionDef, user?.role);

  return (
    <div className="light-section" style={{ display: "flex", minHeight: "100vh", background: "#FDFBF7" }}>
      {/* Desktop Sidebar */}
      <div style={{ width: "220px", background: "#0A0A0A", flexShrink: 0, position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 40, flexDirection: "column" }}
        className="hidden lg:flex">
        <div style={{ padding: "20px 16px 14px", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
          <Logo size="sm" variant="dark" />
          <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "rgba(255,255,255,0.35)", letterSpacing: "0.3em", textTransform: "uppercase", marginTop: "8px", fontWeight: 700 }}>ADMIN</div>
        </div>
        <div style={{ height: "1px", background: "rgba(197,160,89,0.15)", margin: "0 20px 12px", flexShrink: 0 }} />
        <nav className="admin-sidebar-nav" style={{ flex: 1, padding: "0 10px", overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
          {SECTION_GROUPS.map((group) => {
            const visibleItems = group.items.filter((s) => canSee(s, user?.role));
            if (visibleItems.length === 0) return null;
            const isOpen = !!openGroups[group.id];
            return (
              <div key={group.id} style={{ marginBottom: "4px" }}>
                <button data-testid={`admin-group-${group.id}`} onClick={() => toggleGroup(group.id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: "6px", padding: "8px 8px",
                           background: "transparent", border: "none", cursor: "pointer",
                           fontFamily: "Manrope", fontSize: "10px", fontWeight: 700,
                           letterSpacing: "0.14em", textTransform: "uppercase",
                           color: "rgba(255,255,255,0.4)" }}>
                  {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span style={{ flex: 1, textAlign: "left" }}>{group.label}</span>
                </button>
                {isOpen && visibleItems.map(({ id, label, icon: Icon, external }) => (
                  external
                    ? <a key={id} data-testid={`admin-nav-${id}`} href={external}
                        target="_blank" rel="noreferrer"
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px 8px 22px", borderRadius: "6px", border: "none", cursor: "pointer", fontFamily: "Manrope", fontSize: "12.5px", fontWeight: 500, marginBottom: "1px", transition: "all 0.15s", textAlign: "left", background: "transparent", color: "rgba(255,255,255,0.55)", textDecoration: "none", boxSizing: "border-box" }}>
                        <Icon size={14} />{label}
                      </a>
                    : <button key={id} data-testid={`admin-nav-${id}`} onClick={() => setSection(id)}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px 8px 22px", borderRadius: "6px", border: "none", cursor: "pointer", fontFamily: "Manrope", fontSize: "12.5px", fontWeight: 500, marginBottom: "1px", transition: "all 0.15s", textAlign: "left", background: section === id ? "rgba(197,160,89,0.14)" : "transparent", color: section === id ? "#C5A059" : "rgba(255,255,255,0.55)" }}>
                        <Icon size={14} />{label}
                      </button>
                ))}
              </div>
            );
          })}
        </nav>
        <div style={{ padding: "12px 10px 14px", borderTop: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
          <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "rgba(255,255,255,0.5)", padding: "4px 12px 10px" }}>{user?.name}</div>
          <a
            data-testid="view-as-client-link"
            href="/app/home"
            target="_blank"
            rel="noopener noreferrer"
            style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(197,160,89,0.25)", cursor: "pointer", background: "rgba(197,160,89,0.06)", color: "#C5A059", fontFamily: "Manrope", fontSize: "13px", marginBottom: "8px", textDecoration: "none", boxSizing: "border-box" }}
            title="Открыть клиентское приложение в новой вкладке"
          >
            <Eye size={15} />Посмотреть как клиент
          </a>
          <button data-testid="admin-change-password-btn"
            onClick={() => setShowChangePwd(true)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", borderRadius: "6px", border: "none", cursor: "pointer", background: "transparent", color: "rgba(255,255,255,0.6)", fontFamily: "Manrope", fontSize: "12.5px", marginBottom: "2px" }}>
            <KeyRound size={14} />Сменить пароль
          </button>
          <button data-testid="admin-logout-btn" onClick={handleLogout} style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "6px", border: "none", cursor: "pointer", background: "transparent", color: "rgba(255,255,255,0.3)", fontFamily: "Manrope", fontSize: "13px" }}>
            <LogOut size={15} />Выйти
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1 }} className="admin-main-content">
        {/* Mobile Header */}
        <div className="flex lg:hidden" style={{ background: "#0A0A0A", padding: "10px 16px", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 30 }}>
          <Logo size="xs" variant="dark" />
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button data-testid="admin-notif-btn-mobile" onClick={() => setShowNotifications(true)}
              style={{ background: "none", border: "none", color: "rgba(197,160,89,0.85)", cursor: "pointer", position: "relative" }}>
              <Bell size={20} />
              {notifUnread > 0 && (
                <span style={{ position: "absolute", top: "-4px", right: "-6px", background: "#DC2626", color: "white",
                               borderRadius: "9px", minWidth: "16px", height: "16px", fontSize: "10px", fontWeight: 800,
                               display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", fontFamily: "Manrope" }}>
                  {notifUnread > 99 ? "99+" : notifUnread}
                </span>
              )}
            </button>
            <button data-testid="admin-mobile-menu-toggle" aria-label={mobileOpen ? "Закрыть меню" : "Открыть меню"} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)} style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="flex lg:hidden" style={{ background: "#111", padding: "8px 10px", borderBottom: "1px solid rgba(197,160,89,0.15)", flexDirection: "column", maxHeight: "78vh", overflowY: "auto" }}>
            {SECTION_GROUPS.map((group) => {
              const visibleItems = group.items.filter((s) => canSee(s, user?.role));
              if (visibleItems.length === 0) return null;
              const isOpen = !!openGroups[group.id];
              return (
                <div key={group.id} style={{ marginBottom: "2px" }}>
                  <button onClick={() => toggleGroup(group.id)}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: "6px", padding: "10px 6px",
                             background: "transparent", border: "none", cursor: "pointer",
                             fontFamily: "Manrope", fontSize: "10px", fontWeight: 700,
                             letterSpacing: "0.14em", textTransform: "uppercase",
                             color: "rgba(255,255,255,0.45)", textAlign: "left" }}>
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span style={{ flex: 1, textAlign: "left" }}>{group.label}</span>
                  </button>
                  {isOpen && visibleItems.map(({ id, label, icon: Icon }) => (
                    <button key={id} data-testid={`mobile-admin-nav-${id}`}
                      onClick={() => { setSection(id); setMobileOpen(false); }}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 10px 10px 24px", border: "none", cursor: "pointer", background: "transparent", color: section === id ? "#C5A059" : "rgba(255,255,255,0.6)", fontFamily: "Manrope", fontSize: "13px", textAlign: "left" }}>
                      <Icon size={15} />{label}
                    </button>
                  ))}
                </div>
              );
            })}
            {/* Mobile-only bottom block: mirrors the desktop sidebar footer so
                admins can switch to client view or sign out without a desktop. */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: "6px", paddingTop: "6px" }}>
              <a
                data-testid="mobile-view-as-client-link"
                href="/app/home"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "12px", borderRadius: "6px", border: "1px solid rgba(197,160,89,0.25)", background: "rgba(197,160,89,0.06)", color: "#C5A059", fontFamily: "Manrope", fontSize: "14px", textAlign: "left", textDecoration: "none", boxSizing: "border-box", marginBottom: "6px" }}
              >
                <Eye size={16} />Посмотреть как клиент
              </a>
              <button
                data-testid="mobile-admin-logout-btn"
                onClick={() => { setMobileOpen(false); handleLogout(); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "12px", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "6px", cursor: "pointer", background: "rgba(239,68,68,0.06)", color: "#fca5a5", fontFamily: "Manrope", fontSize: "14px", textAlign: "left", boxSizing: "border-box" }}
              >
                <LogOut size={16} />Выйти{user?.email ? ` (${user.email})` : ""}
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div style={{ padding: "28px 24px", minHeight: "calc(100vh - 56px)" }}>
          <OnShiftBar myId={user?.id} />
          <TodayTasksBar />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", gap: "12px", flexWrap: "wrap" }}>
            <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", color: "#0A0A0A", fontWeight: 400, margin: 0 }}>
              {SECTIONS.find(s => s.id === section)?.label}
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" }}>
              <DutyToggle />
              {/* Desktop-only bell (mobile has it in the top header) */}
              <button data-testid="admin-notif-btn-desktop" onClick={() => setShowNotifications(true)}
                className="hidden lg:flex"
                title="Уведомления"
                style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 10px",
                         cursor: "pointer", color: "#C5A059", alignItems: "center", gap: "6px",
                         fontFamily: "Manrope", fontSize: "12px", position: "relative" }}>
                <Bell size={16} /> Уведомления
                {notifUnread > 0 && (
                  <span data-testid="notif-unread-badge"
                    style={{ background: "#DC2626", color: "white", borderRadius: "9px", minWidth: "17px", height: "17px",
                             fontSize: "10px", fontWeight: 800, display: "inline-flex", alignItems: "center",
                             justifyContent: "center", padding: "0 4px" }}>
                    {notifUnread > 99 ? "99+" : notifUnread}
                  </span>
                )}
              </button>
            </div>
          </div>
          {!sectionAllowed && (
            <div data-testid="section-no-access"
                 style={{ background: "white", border: "1px dashed #e5e7eb", borderRadius: "8px",
                          padding: "48px 24px", textAlign: "center", fontFamily: "Manrope",
                          color: "#64748b", fontSize: "14px" }}>
              Нет доступа к этому разделу.
              <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "6px" }}>
                Поступления и закупочные цены доступны только администратору и бухгалтеру.
              </div>
            </div>
          )}
          {sectionAllowed && (<>
          {section === "dashboard" && <AdminDashboard />}
          {section === "analytics" && <AdminAnalytics />}
          {section === "crm" && <AdminCrm />}
          {section === "atelier" && <AdminAtelier />}
          {section === "broadcast" && <AdminBroadcast />}
          {section === "meta_ads" && <AdminMetaAds />}
          {section === "video_lessons" && <AdminVideoLessons />}
          {section === "clients" && <AdminClients />}
          {section === "products" && <AdminProducts />}
          {section === "categories" && <AdminCategories />}
          {section === "brands" && <AdminBrands />}
          {section === "news" && <AdminNews />}
          {section === "appointments" && <AdminAppointments />}
          {section === "birthdays" && <AdminBirthdays />}
          {section === "reply_templates" && <AdminReplyTemplates />}
          {section === "knowledge_base" && <AdminKnowledgeBase />}
          {section === "autopilot" && <AdminAutopilot />}
          {section === "shift_history" && <AdminShiftHistory />}
          {section === "import" && <AdminImport />}
          {section === "customers_import" && <AdminCustomerImport />}
          {section === "arrivals" && <AdminArrivals />}
          {section === "stock" && <AdminStock />}
          {section === "inventory_audit" && <AdminInventoryAudit />}
          {section === "accounting" && <AdminAccounting />}
          {section === "invoices" && <AdminInvoices />}
          {section === "staff" && <AdminStaff currentUserEmail={user?.email} />}
          {section === "translations" && <AdminTranslations />}
          {section === "security" && <AdminSecurity />}
          {section === "push_test" && <AdminPushTest />}
          </>)}
        </div>
      </div>
      {showNotifications && <NotificationsPanel onClose={() => { setShowNotifications(false); fetchUnread(); }} />}
      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.length < 8) { setError("Новый пароль должен быть минимум 8 символов"); return; }
    if (newPwd !== confirmPwd) { setError("Пароли не совпадают"); return; }
    if (newPwd === currentPwd) { setError("Новый пароль должен отличаться от текущего"); return; }
    setBusy(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPwd, new_password: newPwd,
      }, { withCredentials: true });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Не удалось сменить пароль");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                zIndex: 200, display: "flex", alignItems: "center",
                justifyContent: "center", padding: 20 }}>
      <form data-testid="change-password-modal" onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: 28,
                  width: "100%", maxWidth: 420,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center",
                       justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, color: "#0A0A0A" }}>
            Смена пароля
          </div>
          <button type="button" onClick={onClose}
            style={{ background: "transparent", border: "none",
                      cursor: "pointer", color: "#888" }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 18 }}>
          После смены пароль больше не будет сбрасываться при обновлениях системы.
        </div>

        {done ? (
          <div data-testid="change-password-success"
            style={{ padding: 12, background: "#ecfdf5",
                      border: "1px solid #a7f3d0", borderRadius: 8,
                      color: "#065f46", fontSize: 13 }}>
            ✓ Пароль успешно обновлён
          </div>
        ) : (
          <>
            {[
              ["current-pwd", "Текущий пароль", currentPwd, setCurrentPwd],
              ["new-pwd",     "Новый пароль (мин. 8 символов)", newPwd, setNewPwd],
              ["confirm-pwd", "Повторите новый пароль", confirmPwd, setConfirmPwd],
            ].map(([id, label, val, setter]) => (
              <div key={id} style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11,
                                 textTransform: "uppercase", letterSpacing: 0.5,
                                 color: "#8a8a89", marginBottom: 4, fontWeight: 700 }}>
                  {label}
                </label>
                <input data-testid={id} type="password"
                  value={val} onChange={(e) => setter(e.target.value)}
                  autoComplete="new-password"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6,
                            border: "1px solid #e2e0d7", fontSize: 14,
                            fontFamily: "Manrope", background: "white",
                            color: "#0A0A0A", boxSizing: "border-box" }} />
              </div>
            ))}

            {error && (
              <div data-testid="change-password-error"
                style={{ padding: 10, background: "#fef2f2",
                          border: "1px solid #fecaca", borderRadius: 6,
                          color: "#7f1d1d", fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button data-testid="change-password-submit" type="submit" disabled={busy}
              style={{ width: "100%",
                        background: "linear-gradient(135deg,#C5A059,#D4B878)",
                        color: "#0A0A0A", border: "none", borderRadius: 8,
                        padding: "12px 24px", fontWeight: 800, fontSize: 13,
                        textTransform: "uppercase", letterSpacing: 0.5,
                        cursor: busy ? "not-allowed" : "pointer",
                        opacity: busy ? 0.6 : 1 }}>
              {busy ? "Сохраняем..." : "Сменить пароль"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
